// =============================================================================
// src/views/mapa-eleitoral/index.ts — AppModule da Tela 01 (Mapa Eleitoral).
//
// Proveniencia: assets/unified-app.js Sections.mapaEleitoral.activate / deactivate
//   (linhas 859-875, 1037-1047) + unified.html #section-mapa-eleitoral. Une os
//   submodulos (markup/overlays/styles/playback/controls) no contrato AppModule.
//
// Nota de contrato: o original e um SectionController (activate(map)/deactivate()).
//   Aqui expomos render()+init(ctx) (AppModule). init() roda o equivalente a
//   activate quando o mapa ja existe; o ciclo deactivate (parar play, remover
//   overlays) fica disponivel via activateMapaEleitoral/deactivateMapaEleitoral
//   para quem orquestrar scroll-spy, preservando o comportamento original.
//
// Leaflet: esta unidade NAO faz chamadas diretas a API Leaflet — apenas passa
//   funcoes de PathOptions a MapController.setZoneStyles/setMunStyles. Os tipos
//   Leaflet ja foram verificados via Context7 (/leaflet/leaflet, 2026-05-30) em
//   src/app/types.ts; nenhuma nova superficie de API e introduzida aqui.
// =============================================================================

import type {
  AppModule,
  AppContext,
  MapController,
  ElectoralYear,
  LegendLayerKey,
} from '../../app/types';
import { renderMapaEleitoralMarkup } from './markup';
import { buildMapaOverlays, removeMapaOverlays } from './overlays';
import { applyMapaEleitoralStyles } from './styles';
import { MapaTimelinePlayback } from './playback';
import { createMapaTimelinePlayback, bindMapaEleitoralControls } from './controls';

/**
 * Campos mutaveis de estilo do MapController que o original zera antes de aplicar
 * (mapCtrl.currentZoneStyleFn/currentMunStyleFn = null). Nao fazem parte da
 * superficie publica do contrato, mas o codigo original os toca — extensao local
 * para preservar o reset exato sem alargar o contrato compartilhado.
 */
interface MapControllerStyleState {
  currentZoneStyleFn: unknown;
  currentMunStyleFn: unknown;
}

/** Estado vivo da secao (camada/ano atuais) — equivalente aos campos do Sections.mapaEleitoral. */
let activeMapaLayer: LegendLayerKey = 'partido';
let activeMapaYear: ElectoralYear = '2024';
let activeMapaPlayer: MapaTimelinePlayback | null = null;

/** Zera as funcoes de estilo no mapa antes de reaplicar (replica o topo de activate). */
function resetMapStyleFns(mapCtrl: MapController): void {
  const styleState = mapCtrl as MapController & MapControllerStyleState;
  styleState.currentZoneStyleFn = null;
  styleState.currentMunStyleFn = null;
}

/**
 * Ativa a secao no mapa (replica Sections.mapaEleitoral.activate). Reset de estilos,
 * reaplicacao do choropleth, injecao dos overlays e binding dos controles. Idempotente
 * via bindOnce nos controles.
 */
export function activateMapaEleitoral(mapCtrl: MapController): void {
  resetMapStyleFns(mapCtrl);
  applyMapaEleitoralStyles(mapCtrl, () => activeMapaLayer, () => activeMapaYear);
  buildMapaOverlays(activeMapaLayer, activeMapaYear);
  activeMapaPlayer = createMapaTimelinePlayback(mapCtrl, () => activeMapaLayer);
  bindMapaEleitoralControls(
    mapCtrl,
    activeMapaPlayer,
    (layer) => {
      activeMapaLayer = layer;
    },
    () => activeMapaLayer,
  );
}

/** Desativa a secao (replica deactivate): para o play silenciosamente e remove overlays. */
export function deactivateMapaEleitoral(): void {
  if (activeMapaPlayer) activeMapaPlayer.stopSilently();
  removeMapaOverlays();
}

/**
 * AppModule da Tela 01. render() emite a marcacao exata; init(ctx) liga o mapa
 * quando ele ja existe (o mapa monta apos fetch assincrono do GeoJSON — guard
 * para ctx.map === null preserva o cenario "mapa ainda nao pronto").
 */
export const MapaEleitoralView: AppModule = {
  id: 'mapa-eleitoral',

  render(): string {
    return renderMapaEleitoralMarkup();
  },

  init(ctx: AppContext): void {
    if (!ctx.map) return;
    activateMapaEleitoral(ctx.map);
  },
};

export default MapaEleitoralView;
